<template>
  <Navbar :title="title" :class="{'show' : showNavbar}">
    <template v-slot:close>
      <div @click="$emit('close')" class="close">
        Cerrar
      </div>
    </template>
    <template v-slot:content>
      <!--Tenant context (read-only)-->
      <div class="form-group">
        <label>Inquilino</label>
        <input class="input" type="text" :value="data.alias || data.name || '-'" readonly disabled>
      </div>
      <!--Account info (read-only)-->
      <div class="form-group">
        <label for="loginEmail">Email de login</label>
        <input class="input" type="text" id="loginEmail" :value="account.email || '-'" readonly disabled>
      </div>
      <div class="form-group">
        <label>Estado</label>
        <input class="input" type="text"
               :value="account.active ? 'Activo' : 'Inactivo'" readonly disabled>
      </div>
      <!--Reset password-->
      <div class="manage-section">
        <h3 class="manage-section__title">Resetear contraseña</h3>
        <div class="form-group">
          <label for="newPassword">Nueva contraseña</label>
          <input v-model.trim="form.password" class="input" type="text" id="newPassword" maxlength="64">
          <span v-if="validationErrors.password" class="error">{{ validationErrors.password }}</span>
          <div @click="generatePassword" class="generate-password">Generar contraseña</div>
        </div>
        <button :disabled="progress" :class="{'is-loading': progress}" @click="reset" class="btn">
          <span v-if="!progress">Resetear contraseña</span>
        </button>
      </div>
      <!--Revoke access-->
      <div class="manage-section">
        <h3 class="manage-section__title">Revocar acceso</h3>
        <template v-if="!confirmingRevoke">
          <button :disabled="progress" :class="{'is-loading': progress}"
                  @click="confirmingRevoke = true" class="btn btn--danger">
            <span v-if="!progress">Revocar acceso</span>
          </button>
        </template>
        <template v-else>
          <p class="manage-confirm__text">¿Seguro? El expositor ya no podrá ingresar</p>
          <div class="manage-confirm__actions">
            <button :disabled="progress" :class="{'is-loading': progress}"
                    @click="revoke" class="btn btn--danger">
              <span v-if="!progress">Sí, revocar</span>
            </button>
            <button :disabled="progress" @click="confirmingRevoke = false" class="btn btn--outline">
              Cancelar
            </button>
          </div>
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div @click="$emit('close')" class="btn btn--outline">Cerrar</div>
    </template>
  </Navbar>
  <Shadow :class="{'show' : showNavbar}"/>
</template>

<script>
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import { functions } from '@/firebase/firebaseConfig'
import * as yup from 'yup'

yup.setLocale({
  mixed: {
    required: 'Este campo es obligatorio'
  }
})

export default {
  name: 'ManageAccess',
  props: {
    showNavbar: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    title: {
      type: String
    },
    data: {
      type: Object,
      required: true
    }
  },
  components: {
    Navbar,
    Shadow
  },
  data () {
    return {
      account: {
        email: null,
        active: false
      },
      form: {
        password: ''
      },
      schema: yup.object().shape({
        password: yup.string().required().min(6, 'Mínimo 6 caracteres').max(64, 'Máximo 64 caracteres').label('Contraseña')
      }),
      validationErrors: {},
      confirmingRevoke: false,
      progress: false
    }
  },
  watch: {
    showNavbar (value) {
      if (value) {
        this.form = { password: '' }
        this.validationErrors = {}
        this.confirmingRevoke = false
        this.account = { email: null, active: false }
        this.loadAccess()
      }
    }
  },
  methods: {
    async loadAccess () {
      try {
        this.progress = true
        const callable = functions.httpsCallable('exhibitors-getExhibitorAccess')
        const result = await callable({ clientId: this.data.id })
        this.account = {
          email: result.data.email,
          active: result.data.active
        }
      } catch (err) {
        this.handleError(err, 'Error al cargar el acceso')
        console.log(err)
      } finally {
        this.progress = false
      }
    },
    generatePassword () {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
      let password = ''
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      this.form.password = password
    },
    async reset () {
      try {
        this.progress = true
        this.validationErrors = {}
        await this.schema.validate({ ...this.form }, { abortEarly: false })
        const callable = functions.httpsCallable('exhibitors-resetExhibitorPassword')
        await callable({
          clientId: this.data.id,
          password: this.form.password
        })
        this.$toast.success('Contraseña actualizada')
        this.form = { password: '' }
        this.$emit('reset', { clientId: this.data.id })
      } catch (err) {
        if (err.inner) {
          err.inner.forEach(error => {
            if (error.path) {
              this.validationErrors[error.path] = error.message
            }
          })
        } else {
          this.handleError(err, 'Error al resetear la contraseña')
        }
        console.log(err)
      } finally {
        this.progress = false
      }
    },
    async revoke () {
      try {
        this.progress = true
        const callable = functions.httpsCallable('exhibitors-revokeExhibitorAccess')
        await callable({ clientId: this.data.id })
        this.$toast.success('Acceso revocado')
        this.$emit('revoked', { clientId: this.data.id })
        this.confirmingRevoke = false
        this.$emit('close')
      } catch (err) {
        this.handleError(err, 'Error al revocar el acceso')
        console.log(err)
      } finally {
        this.progress = false
      }
    },
    handleError (err, fallback) {
      switch (err.code) {
        case 'functions/permission-denied':
          this.$toast.error('No tienes permiso para gestionar accesos')
          break
        case 'functions/invalid-argument':
          this.$toast.error('Datos inválidos, revisa la información')
          break
        case 'functions/not-found':
          this.$toast.error('No se encontró el acceso del expositor')
          break
        case 'functions/failed-precondition':
          this.$toast.error('El expositor no tiene un acceso activo')
          break
        default:
          this.$toast.error(fallback)
      }
    }
  }
}
</script>

<style scoped>
.generate-password {
  display: inline-block;
  margin-top: 8px;
  font-size: 13px;
  color: #B51A3E;
  cursor: pointer;
  text-decoration: underline;
}

.manage-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.manage-section__title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
}

.manage-confirm__text {
  margin: 0 0 12px;
  font-size: 14px;
  color: #555;
}

.manage-confirm__actions {
  display: flex;
  gap: 8px;
}

.btn--danger {
  background: #B51A3E;
  color: #fff;
}
</style>
